// Org users — the org admin invites engineers (who build agents) and users (who
// chat with published agents), or further admins.
//
// Everyone signs in the same way an admin does: the org login page, Google SSO
// where the org has it, a temp password changed on first sign-in. What differs
// is what the console shows them. Changing someone's role or access signs them
// out everywhere, so a demotion takes effect immediately rather than when their
// cookie happens to expire.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2, UserPlus } from "lucide-react";
import * as React from "react";

import { CredentialDialog, type Credential } from "@/components/credential-dialog";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  Input,
  Label,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  selectClass,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { createMember, deleteMember, resetMemberPassword, updateMember } from "@/lib/api/endpoints";
import { useMe, useMembers } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { Member, MemberRole } from "@/types/api";

const ROLE_LABEL: Record<MemberRole, string> = {
  tenant_admin: "Admin",
  engineer: "Engineer",
  evaluator: "Evaluator",
  user: "User",
};

const ROLE_HINT: Record<MemberRole, string> = {
  tenant_admin: "Everything, including users and org settings",
  engineer: "Builds agents, knowledge bases, tools and models",
  evaluator: "Chats with published agents to rate their answers",
  user: "Chats with published agents",
};

const ROLES: MemberRole[] = ["user", "evaluator", "engineer", "tenant_admin"];

export default function UsersPage() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { data, isLoading, isError, error, refetch } = useMembers();
  const [showForm, setShowForm] = React.useState(false);
  const [credential, setCredential] = React.useState<Credential | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<Member | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.members });

  const update = useMutation({
    mutationFn: (v: { id: number; role?: MemberRole; is_active?: boolean }) =>
      updateMember(v.id, { role: v.role, is_active: v.is_active }),
    onSuccess: async (m) => {
      await invalidate();
      toast.success(`${m.email} updated — they will need to sign in again.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reset = useMutation({
    mutationFn: (m: Member) => resetMemberPassword(m.id),
    onSuccess: async (res) => {
      await invalidate();
      setCredential({ email: res.member.email, password: res.temp_password });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (m: Member) => deleteMember(m.id),
    onSuccess: async (_r, m) => {
      await invalidate();
      setPendingDelete(null);
      toast.success(`${m.email} removed`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div>
      <PageHeader
        title="Users"
        description="People in your organisation. Engineers build agents; users chat with the agents you publish."
        actions={
          <Button onClick={() => setShowForm((v) => !v)}>
            <UserPlus className="h-4 w-4" />
            Invite user
          </Button>
        }
      />

      {showForm ? (
        <InviteForm
          onDone={async (res) => {
            await invalidate();
            setShowForm(false);
            setCredential(res);
          }}
        />
      ) : null}

      {isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Card className="p-4">
          <Skeleton className="h-32 w-full" />
        </Card>
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          title="No users yet"
          hint="Invite engineers to build agents and users to chat with them."
          action={<Button onClick={() => setShowForm(true)}>Invite user</Button>}
        />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Email</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH className="w-px" />
              </TR>
            </THead>
            <TBody>
              {(data ?? []).map((m) => {
                const self = m.id === me?.admin_id;
                return (
                  <TR key={m.id}>
                    <TD>
                      <div className="font-medium">{m.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {self ? "You" : m.google_linked ? "Google linked" : "Password"}
                      </div>
                    </TD>
                    <TD>
                      <select
                        aria-label={`Role for ${m.email}`}
                        className={selectClass}
                        value={m.role}
                        disabled={self || update.isPending}
                        onChange={(e) =>
                          update.mutate({ id: m.id, role: e.target.value as MemberRole })
                        }
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    </TD>
                    <TD>
                      {!m.is_active ? (
                        <Badge variant="secondary">Deactivated</Badge>
                      ) : m.must_change_password ? (
                        <Badge variant="outline">Invited</Badge>
                      ) : (
                        <Badge variant="success">Active</Badge>
                      )}
                    </TD>
                    <TD>
                      {self ? null : (
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={update.isPending}
                            onClick={() => update.mutate({ id: m.id, is_active: !m.is_active })}
                          >
                            {m.is_active ? "Deactivate" : "Reactivate"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={reset.isPending}
                            onClick={() => reset.mutate(m)}
                          >
                            <KeyRound className="h-4 w-4" />
                            Reset password
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPendingDelete(m)}
                            aria-label={`Remove ${m.email}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </Card>
      )}

      <CredentialDialog credential={credential} onClose={() => setCredential(null)} />
      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Remove ${pendingDelete?.email ?? ""}?`}
        description="They are signed out and can no longer sign in. Their past conversations are kept."
        confirmLabel="Remove"
        pending={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
      />
    </div>
  );
}

function InviteForm({ onDone }: { onDone: (c: Credential) => void | Promise<void> }) {
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<MemberRole>("user");

  const create = useMutation({
    mutationFn: () => createMember(email.trim(), role),
    onSuccess: (res) => onDone({ email: res.member.email, password: res.temp_password }),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card className="mb-6 p-4">
      <form
        className="grid gap-4 sm:grid-cols-[1fr_220px_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="invite-email">Email</Label>
          <Input
            id="invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="invite-role">Role</Label>
          <select
            id="invite-role"
            className={selectClass}
            value={role}
            onChange={(e) => setRole(e.target.value as MemberRole)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]} — {ROLE_HINT[r]}
              </option>
            ))}
          </select>
        </div>
        <Button type="submit" disabled={create.isPending || !email.trim()}>
          <Plus className="h-4 w-4" />
          {create.isPending ? "Inviting…" : "Invite"}
        </Button>
      </form>
      <p className="mt-3 text-xs text-muted-foreground">
        They get a one-time password to change on first sign-in, or can use Google if your
        organisation has it set up.
      </p>
    </Card>
  );
}
